import type { HomePageCarouselItem } from '@bulknutrients/bulk-shared-lib/interfaces/ICarousel';
import { Icons } from '@components/Icons/Icons';
import useEmblaCarousel from 'embla-carousel-react';
import { type ReactNode, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';
import { CarouselItem } from './CarouselItem';

function Carousel({ slides }: { slides: ReactNode[] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return slides.length > 0 ? (
    <div className={twJoin(['embla', 'relative'])}>
      <div
        className={twJoin(['embla__viewport', 'overflow-hidden'])}
        ref={emblaRef}
      >
        <div className={twJoin(['embla__container', 'flex'])}>
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={twJoin(['embla__slide', 'grow-0 shrink-0 basis-full'])}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className={twJoin([
          'embla__prev',
          'absolute left-3 top-[50vw] sm:top-0 sm:bottom-0 sm:my-auto rounded-full bg-white w-12 h-12 text-grey-dark border-grey-dark border-1',
        ])}
        onClick={scrollPrev}
      >
        <Icons.Chevron.Left size={24} />
      </button>
      <button
        type="button"
        className={twJoin([
          'embla__next',
          'absolute right-3 top-[50vw] sm:top-0 sm:bottom-0 sm:my-auto rounded-full bg-white w-12 h-12 text-grey-dark border-grey-dark border-1',
        ])}
        onClick={scrollNext}
      >
        <Icons.Chevron.Right size={24} />
      </button>
    </div>
  ) : null;
}

export function HomePageCarousel({
  carouselItems,
}: {
  carouselItems: Array<HomePageCarouselItem>;
}) {
  const slides = carouselItems
    ? carouselItems
        .sort((a, b) => a.position - b.position) // sort slide based on position
        .map((child, idx) => (
          <CarouselItem
            key={idx}
            carouselItem={child}
            priority={idx === 0} // only prioritise first slide
          />
        ))
    : [];

  return <Carousel slides={slides} />;
}
